import {
  allFormFieldTypes,
  type Form,
  FormFieldType,
  generateFormFieldId,
} from "@repo/app-types/entities/form";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { CreatableComboboxField } from "@repo/ui/combobox/creatableComboboxField";
import { DraggableItem } from "@repo/ui/DraggableList/draggableItem";
import { dropdownMenu } from "@repo/ui/Dropdown/dropdownMenuStyles";
import { Size } from "@repo/ui/helpers/helpers";
import { useDraggableList } from "@repo/ui/hooks/useDraggableList";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import { Slideover } from "@repo/ui/Slideover/slideover";
import { IconPlus } from "@tabler/icons-react";
import isEqual from "lodash-es/isEqual.js";
import type React from "react";
import { type FC, useEffect, useState } from "react";
import {
  Menu as AriaMenu,
  MenuItem,
  MenuTrigger,
  Popover,
} from "react-aria-components";
import {
  useCreateFormMutation,
  useUpdateFormMutation,
} from "../../../api/entities/form.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import {
  FieldFormQuestionCard,
  type FormFieldWithValidation,
  getQuestionTypeLabel,
} from "./questions/card.js";

// Form name validation constants
const FORM_NAME_MIN_LENGTH = 5;
const FORM_NAME_MAX_LENGTH = 200;

interface PreInterviewFormEditorSlideoverProps {
  form?: Form;
  isOpen: boolean;
  onClose: () => void;
}

export const PreInterviewFormEditorSlideover: FC<
  PreInterviewFormEditorSlideoverProps
> = ({ form, isOpen, onClose }) => {
  const [createForm, { isLoading: isCreating }] = useCreateFormMutation();
  const [updateForm, { isLoading: isUpdating }] = useUpdateFormMutation();

  const isEditing = !!form;
  const [formName, setFormName] = useState(form?.name || "");
  const [formNameErrorMessage, setFormNameErrorMessage] = useState<
    string | undefined
  >();
  const [hasChanges, setHasChanges] = useState(!isEditing);

  // Track form fields and any errors that occur while editing them.
  const [formFields, setFormFields] = useState<
    readonly FormFieldWithValidation[]
  >([]);

  // Reset form state when form prop changes
  useEffect(() => {
    if (form) {
      setFormName(form.name);
      setFormNameErrorMessage(undefined);
      setFormFields(
        form.settings.fields.map((field) => ({
          ...field,
          errorMessage: undefined,
        })),
      );
      setHasChanges(false);
    } else {
      // When creating a new form, initialize with an empty dropdown field
      setFormName("");
      setFormNameErrorMessage(undefined);
      setFormFields([
        {
          id: generateFormFieldId(),
          type: FormFieldType.Dropdown,
          label: "",
          options: [],
          errorMessage: undefined,
        },
      ]);
      setHasChanges(true);
    }
  }, [form]);

  // Track changes to formName and formFields
  useEffect(() => {
    if (isEditing) {
      // Check if name has changed
      const nameChanged = formName !== form?.name;

      // Check if fields have changed
      const fieldsChanged = !isEqual(formFields, form?.settings.fields ?? []);

      setHasChanges(nameChanged || fieldsChanged);
    }
  }, [formName, formFields, isEditing, form]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormName(e.target.value);
    // Clear error message when user types
    if (formNameErrorMessage) {
      setFormNameErrorMessage(undefined);
    }
  };

  const validateFormName = () => {
    if (formName.trim().length < FORM_NAME_MIN_LENGTH) {
      setFormNameErrorMessage(
        `Form name must be at least ${FORM_NAME_MIN_LENGTH} characters`,
      );
      return false;
    } else if (formName.trim().length > FORM_NAME_MAX_LENGTH) {
      setFormNameErrorMessage(
        `Form name must be less than ${FORM_NAME_MAX_LENGTH} characters`,
      );
      return false;
    } else {
      setFormNameErrorMessage(undefined);
      return true;
    }
  };

  const handleSave = async () => {
    try {
      // Validate form name before saving
      if (!validateFormName()) {
        return;
      }

      // Prevent save if errors (should be handled by isDisabled as well).
      if (formFields.some((q) => q.errorMessage)) {
        return;
      }

      if (isEditing) {
        await updateForm({
          id: form.id,
          name: formName,
          settings: {
            fields: formFields,
          },
        }).unwrap();
      } else {
        await createForm({
          name: formName,
          settings: {
            fields: formFields,
          },
        }).unwrap();
      }
      closeAndResetSlideover();
    } catch (error) {
      console.error(
        `Failed to ${isEditing ? "update" : "create"} form:`,
        error,
      );
    }
  };

  const closeAndResetSlideover = () => {
    setFormName("");
    setFormNameErrorMessage(undefined);
    setFormFields([]);
    setHasChanges(false);
    onClose();
  };

  const handleAddField = (type: FormFieldType) => {
    switch (type) {
      case FormFieldType.Dropdown: {
        const newField: FormFieldWithValidation = {
          id: generateFormFieldId(),
          type,
          label: "",
          options: [],
          errorMessage: undefined,
        };

        setFormFields([...formFields, newField]);
        break;
      }
      default: {
        const newField: FormFieldWithValidation = {
          id: generateFormFieldId(),
          type,
          label: "",
          errorMessage: undefined,
        };

        setFormFields([...formFields, newField]);
        break;
      }
    }
  };

  const handleFieldChange = (
    index: number,
    updatedField: FormFieldWithValidation,
  ) => {
    setFormFields(formFields.map((f, i) => (i === index ? updatedField : f)));
  };

  const handleFieldDelete = (index: number) => {
    setFormFields(formFields.filter((_, i) => i !== index));
  };

  const {
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    handleDragLeave,
    dragOverIndex,
  } = useDraggableList<FormFieldWithValidation>({
    items: formFields,
    onReorder: (newFields) => {
      setFormFields([...newFields]);
      setHasChanges(true);
    },
  });

  const isSaving = isEditing ? isUpdating : isCreating;
  const pageTitle = isEditing ? "Edit form" : "Create form";
  const permissionAction = isEditing
    ? PermissionActions.UPDATE
    : PermissionActions.CREATE;

  const getIsSaveDisabled = (hasPermission: boolean) => {
    const baseConditions = !hasChanges || !hasPermission || isSaving;

    const isMissingName = !formName.trim();
    // If there are any errors in our name or fields, disable save.
    const hasFormNameError = !!formNameErrorMessage;
    const hasFormFieldsError = formFields.some((f) => f.errorMessage);
    const hasInvalidFormFieldsCount =
      formFields.length < 1 || formFields.length > 10;

    return (
      baseConditions ||
      isMissingName ||
      hasFormNameError ||
      hasFormFieldsError ||
      hasInvalidFormFieldsCount
    );
  };

  const renderButtons = (hasPermission: boolean) => (
    <>
      <Button
        variant={ButtonVariant.Primary}
        onClick={handleSave}
        isDisabled={getIsSaveDisabled(hasPermission)}
        isLoading={isSaving}
      >
        {isEditing ? "Save changes" : "Create form"}
      </Button>
      <Button
        variant={ButtonVariant.Secondary}
        onClick={closeAndResetSlideover}
        isDisabled={!hasPermission}
      >
        Cancel
      </Button>
    </>
  );

  const renderCustomFormField = (
    index: number,
    field: FormFieldWithValidation,
  ) => {
    if (field.type === FormFieldType.Dropdown) {
      return (
        <CreatableComboboxField
          label="Response options"
          placeholder="Add a response option"
          value={field.options.map((option) => option.value)}
          onChange={(values) => {
            const updatedField = {
              ...field,
              options: values.map((value) => ({ value })),
              errorMessage: undefined,
            };
            handleFieldChange(index, updatedField);
          }}
        />
      );
    }

    // Other field types (only "text_input" for now) do not require any additional custom setup.
    return null;
  };

  const renderQuestionCards = () => {
    return formFields.map((field, index) => (
      // We only support dropdowns for now.
      <DraggableItem
        key={field.id}
        index={index}
        dragOverIndex={dragOverIndex}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragEnd={handleDragEnd}
        onDragLeave={handleDragLeave}
      >
        <FieldFormQuestionCard
          index={index}
          field={field}
          onChange={(updatedQuestion) =>
            handleFieldChange(index, updatedQuestion)
          }
          onDelete={() => handleFieldDelete(index)}
        >
          {renderCustomFormField(index, field)}
        </FieldFormQuestionCard>
      </DraggableItem>
    ));
  };

  return (
    <Slideover
      shouldShow={isOpen}
      onClose={closeAndResetSlideover}
      title={pageTitle}
      size={Size.SMALL}
      buttons={
        <Can
          I={permissionAction}
          a={PermissionSubjects.FORMS}
          passThrough={true}
        >
          {(hasPermission) => renderButtons(hasPermission)}
        </Can>
      }
    >
      <div className="h-full w-[500px] space-y-4 overflow-y-auto bg-background p-4">
        <div>
          <Can
            I={permissionAction}
            a={PermissionSubjects.FORMS}
            passThrough={true}
          >
            {(hasPermission) => (
              <div className="space-y-3">
                <div>
                  <Label size={Size.MEDIUM} className="mb-2">
                    Name
                  </Label>
                  <Input
                    id="form-name"
                    placeholder="Pre-interview form"
                    value={formName}
                    onChange={handleNameChange}
                    onBlur={validateFormName}
                    isDisabled={!hasPermission}
                    minLength={FORM_NAME_MIN_LENGTH}
                    maxLength={FORM_NAME_MAX_LENGTH}
                    errorDescription={formNameErrorMessage}
                    inputClassName="w-full"
                  />
                </div>

                <div className="mt-2">
                  <Label size={Size.MEDIUM} className="mb-2">
                    Questions
                  </Label>

                  <div>{renderQuestionCards()}</div>

                  <MenuTrigger>
                    <Button
                      isDisabled={!hasPermission}
                      className="mt-4"
                      variant={ButtonVariant.Secondary}
                      icon={<IconPlus className="mr-1 h-4 w-4" />}
                    >
                      Add question
                    </Button>

                    <Popover className={dropdownMenu().popover()}>
                      <AriaMenu className={dropdownMenu().menu()}>
                        {allFormFieldTypes.map((fieldType) => {
                          return (
                            <MenuItem
                              key={fieldType}
                              className={dropdownMenu().item()}
                              onAction={() => handleAddField(fieldType)}
                            >
                              {getQuestionTypeLabel(fieldType)}
                            </MenuItem>
                          );
                        })}
                      </AriaMenu>
                    </Popover>
                  </MenuTrigger>
                </div>
              </div>
            )}
          </Can>
        </div>
      </div>
    </Slideover>
  );
};
